import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	createRawRichTextContentStateWithHtmlStringValue,
	ImpersonationBroker,
	type INote,
	type IRawRichTextContentState,
	RichContentReferenceMethod,
} from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

export interface SaveCallNoteVariables {
	/** Phone call id, or `'manual'` for a manual log. Drives URL endpoint selection. */
	phoneCallId: string;
	/** Always required — used as the `lead/{id}/Note` fallback URL and the
	 *  `companies` referenced-entity. */
	companyId: string;
	/** HTML string from the rich-text editor. Wrapped into `IRawRichTextContentState`
	 *  before being saved as the note's `content`. */
	contentHtml: string;
	/** Optional CC users — attached to `referencedEntities.users` so the
	 *  server's note-CC routing kicks in. */
	ccUserIds?: string[];
	/** Whether this note is being saved alongside an outcome — sets
	 *  `isForOutcome: true` in the request body so the server tags it
	 *  appropriately. Always true in our wrap-up flow. */
	isForOutcome?: boolean;
}

/**
 * POSTs a call note. Mirrors `AidaNoteViewModel.saveLeadNote`
 * (`aida/viewModels/note.ts`) — multipart/form-data with a `value` field
 * carrying the note JSON. Returns the server-assigned `id` so the
 * downstream outcome save (or skip) can reference it.
 *
 * URL selection mirrors `QueueNoteEditor.onSaveClick`:
 * - Real phone call (phoneCallId !== 'manual'): `phonecall/{id}/Note`
 * - Manual log: `lead/{companyId}/Note`
 *
 * The note's referenced entities always include the lead's company; CC
 * users are appended when present so the server's CC routing kicks in.
 *
 * Note shape: the server expects `content` as `IRawRichTextContentState`
 * (the legacy `<lev-content>` wrapper), so we run the incoming HTML
 * through `createRawRichTextContentStateWithHtmlStringValue` here rather
 * than passing the raw string. That matches what
 * `editorState.getRawRichTextContent()` returns in the legacy
 * `QueueNoteEditor.buildNoteToSave` path.
 */
export function useSaveCallNote(): UseMutationResult<INote, Error, SaveCallNoteVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<INote, Error, SaveCallNoteVariables>({
		mutationFn: async ({ phoneCallId, companyId, contentHtml, ccUserIds, isForOutcome }) => {
			if (!companyId) {
				throw new Error('useSaveCallNote: companyId is required');
			}
			const isManual = !phoneCallId || phoneCallId === 'manual';
			const urlPath = isManual ? `lead/${companyId}/Note` : `phonecall/${phoneCallId}/Note`;

			// Wrap HTML in the `<lev-content>` shape the server expects. This
			// matches `editorState.getRawRichTextContent()` in legacy.
			const content: IRawRichTextContentState = createRawRichTextContentStateWithHtmlStringValue(contentHtml ?? '');

			// Mirror legacy `referenced-entities` shape exactly so the server's
			// note-CC routing kicks in.
			const note: Partial<INote> = {
				content,
				// `INote.context` is optional; the legacy editor explicitly nulls
				// it when building a fresh note for a call/skip — preserve that.
				context: null as unknown as INote['context'],
				visibility: 'all',
				referencedEntities: {
					companies: [{ entity: { id: companyId }, method: RichContentReferenceMethod.Explicit }],
					contacts: [],
					users: (ccUserIds ?? []).map(id => ({
						entity: { id },
						method: RichContentReferenceMethod.Explicit,
					})),
				},
			};

			const body = { ...note, companyId, isForOutcome: !!isForOutcome };
			const formData = new FormData();
			formData.append('value', JSON.stringify(body));

			const result = await userSession.webServiceHelper.callAsync<INote>(
				ImpersonationBroker.composeApiUrl({ urlPath }),
				'POST',
				formData
			);

			if (!result || !result.id) {
				// `callAsync` already throws on 4xx/5xx; this is defensive in
				// case the server returns 200 with no id.
				throw new Error('useSaveCallNote: note save returned no id');
			}
			return result;
		},
		onSuccess: (_note, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.leadHistory(variables.companyId) });
		},
	});
}
